import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
// The menu drawer (and with it the whole tool registry) loads the first time ☰ is pressed, not with the page.
const SiteSidebar = lazy(() => import('./components/SiteSidebar'));
import { UpdateBar } from './components/UpdateBar';
import { IosInstallHint } from './components/IosInstallHint';
import AnalyticsTracker from './components/AnalyticsTracker';
import ScrollToTop from './components/ScrollToTop';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { registerServiceWorker, skipWaiting } from './utils/swRegistration';
import { initInstallPrompt } from './utils/installPromptUtils';
import { ALIAS_REDIRECTS } from './data/toolAliases';
import { lazyWithPreload, type PreloadableComponent } from './utils/lazyWithPreload';

// Every route is a separate lazy chunk so a page's own heavy dependency
// (sql-formatter,cron-parser's luxon, js-yaml, ua-parser-js) is only
// downloaded when that specific route is visited, instead of all of them
// loading upfront in the main bundle.
const Home = lazyWithPreload(() => import('./pages/Home'));
const ByteParserPage = lazyWithPreload(() => import('./pages/ByteParserPage'));
const JsonParserPage = lazyWithPreload(() => import('./pages/JsonParserPage'));
const JsonSchemaValidatorPage = lazyWithPreload(() => import('./pages/JsonSchemaValidatorPage'));
const UrlParserPage = lazyWithPreload(() => import('./pages/UrlParserPage'));
const QueryStringPage = lazyWithPreload(() => import('./pages/QueryStringPage'));
const RestApiTesterPage = lazyWithPreload(() => import('./pages/RestApiTesterPage'));
const TimezoneConverterPage = lazyWithPreload(() => import('./pages/TimezoneConverterPage'));
const ColorBlindnessPage = lazyWithPreload(() => import('./pages/ColorBlindnessPage'));
const CssEffectsGeneratorPage = lazyWithPreload(() => import('./pages/CssEffectsGeneratorPage'));
const MetaPreviewPage = lazyWithPreload(() => import('./pages/MetaPreviewPage'));
const ImageResizerPage = lazyWithPreload(() => import('./pages/ImageResizerPage'));
const JsonPathPage = lazyWithPreload(() => import('./pages/JsonPathPage'));
const XmlXsdValidatorPage = lazyWithPreload(() => import('./pages/XmlXsdValidatorPage'));
const CertificateDecoderPage = lazyWithPreload(() => import('./pages/CertificateDecoderPage'));
const NumberByteFormatterPage = lazyWithPreload(() => import('./pages/NumberByteFormatterPage'));
const JsonTomlPage = lazyWithPreload(() => import('./pages/JsonTomlPage'));
const IbanValidatorPage = lazyWithPreload(() => import('./pages/IbanValidatorPage'));
const StringListToolsPage = lazyWithPreload(() => import('./pages/StringListToolsPage'));
const QrCodeGeneratorPage = lazyWithPreload(() => import('./pages/QrCodeGeneratorPage'));
const GuidGeneratorPage = lazyWithPreload(() => import('./pages/GuidGeneratorPage'));
const JsonComparePage = lazyWithPreload(() => import('./pages/JsonComparePage'));
const TextComparePage = lazyWithPreload(() => import('./pages/TextComparePage'));
const JsonBeautifierPage = lazyWithPreload(() => import('./pages/JsonBeautifierPage'));
const BaseConverterPage = lazyWithPreload(() => import('./pages/BaseConverterPage'));
const XmlFormatterPage = lazyWithPreload(() => import('./pages/XmlFormatterPage'));
const EncodeDecodePage = lazyWithPreload(() => import('./pages/EncodeDecodePage'));
const JwtDecoderPage = lazyWithPreload(() => import('./pages/JwtDecoderPage'));
const HashGeneratorPage = lazyWithPreload(() => import('./pages/HashGeneratorPage'));
const PasswordGeneratorPage = lazyWithPreload(() => import('./pages/PasswordGeneratorPage'));
const PasswordStrengthPage = lazyWithPreload(() => import('./pages/PasswordStrengthPage'));
const UnixTimestampConverterPage = lazyWithPreload(() => import('./pages/UnixTimestampConverterPage'));
const CronParserPage = lazyWithPreload(() => import('./pages/CronParserPage'));
const CaseConverterPage = lazyWithPreload(() => import('./pages/CaseConverterPage'));
const SlugGeneratorPage = lazyWithPreload(() => import('./pages/SlugGeneratorPage'));
const YamlJsonConverterPage = lazyWithPreload(() => import('./pages/YamlJsonConverterPage'));
const CurlConverterPage = lazyWithPreload(() => import('./pages/CurlConverterPage'));
const SqlFormatterPage = lazyWithPreload(() => import('./pages/SqlFormatterPage'));
const UserAgentParserPage = lazyWithPreload(() => import('./pages/UserAgentParserPage'));
const SvgOptimizerPage = lazyWithPreload(() => import('./pages/SvgOptimizerPage'));
const ColorConverterPage = lazyWithPreload(() => import('./pages/ColorConverterPage'));
const DummyDataGeneratorPage = lazyWithPreload(() => import('./pages/DummyDataGeneratorPage'));
const LoremIpsumPage = lazyWithPreload(() => import('./pages/LoremIpsumPage'));
const GraphqlFormatterPage = lazyWithPreload(() => import('./pages/GraphqlFormatterPage'));
const CidrCalculatorPage = lazyWithPreload(() => import('./pages/CidrCalculatorPage'));
const HtmlEntityEncoderPage = lazyWithPreload(() => import('./pages/HtmlEntityEncoderPage'));
const DiffCheckerPage = lazyWithPreload(() => import('./pages/DiffCheckerPage'));
const Base64ImagePage = lazyWithPreload(() => import('./pages/Base64ImagePage'));
const FaviconGeneratorPage = lazyWithPreload(() => import('./pages/FaviconGeneratorPage'));
const CspGeneratorPage = lazyWithPreload(() => import('./pages/CspGeneratorPage'));
const RobotsTxtGeneratorPage = lazyWithPreload(() => import('./pages/RobotsTxtGeneratorPage'));
const AesEncryptionPage = lazyWithPreload(() => import('./pages/AesEncryptionPage'));
const Base64UrlConverterPage = lazyWithPreload(() => import('./pages/Base64UrlConverterPage'));
const RsaEncryptionPage = lazyWithPreload(() => import('./pages/RsaEncryptionPage'));
const AboutPage = lazyWithPreload(() => import('./pages/AboutPage'));
const ContactPage = lazyWithPreload(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazyWithPreload(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazyWithPreload(() => import('./pages/TermsPage'));
const NotFoundPage = lazyWithPreload(() => import('./pages/NotFoundPage'));
const UiKitPage = lazyWithPreload(() => import('./pages/UiKitPage'));
const AllToolsPage = lazyWithPreload(() => import('./pages/AllToolsPage'));
const SavedPage = lazyWithPreload(() => import('./pages/SavedPage'));
const PercentageCalculatorPage = lazyWithPreload(() => import('./pages/PercentageCalculatorPage'));
const UnitConverterPage = lazyWithPreload(() => import('./pages/UnitConverterPage'));
const LoanCalculatorPage = lazyWithPreload(() => import('./pages/LoanCalculatorPage'));
const CompoundInterestCalculatorPage = lazyWithPreload(() => import('./pages/CompoundInterestCalculatorPage'));
const ScientificCalculatorPage = lazyWithPreload(() => import('./pages/ScientificCalculatorPage'));
const InvertPdfPage = lazyWithPreload(() => import('./pages/InvertPdfPage'));
const HtmlTableToCsvPage = lazyWithPreload(() => import('./pages/HtmlTableToCsvPage'));
const SrtVttConverterPage = lazyWithPreload(() => import('./pages/SrtVttConverterPage'));
const PdfToSvgPage = lazyWithPreload(() => import('./pages/PdfToSvgPage'));
const PdfFormCreatorPage = lazyWithPreload(() => import('./pages/PdfFormCreatorPage'));
const PythonPlaygroundPage = lazyWithPreload(() => import('./pages/PythonPlaygroundPage'));
const GlslShaderPlaygroundPage = lazyWithPreload(() => import('./pages/GlslShaderPlaygroundPage'));
const PostgresPlaygroundPage = lazyWithPreload(() => import('./pages/PostgresPlaygroundPage'));
const SqlitePlaygroundPage = lazyWithPreload(() => import('./pages/SqlitePlaygroundPage'));
const TailwindPlaygroundPage = lazyWithPreload(() => import('./pages/TailwindPlaygroundPage'));
const SveltePlaygroundPage = lazyWithPreload(() => import('./pages/SveltePlaygroundPage'));
const VuePlaygroundPage = lazyWithPreload(() => import('./pages/VuePlaygroundPage'));
const ReactPlaygroundPage = lazyWithPreload(() => import('./pages/ReactPlaygroundPage'));
const TypescriptPlaygroundPage = lazyWithPreload(() => import('./pages/TypescriptPlaygroundPage'));
const JavascriptPlaygroundPage = lazyWithPreload(() => import('./pages/JavascriptPlaygroundPage'));
const HtmlCssJsPlaygroundPage = lazyWithPreload(() => import('./pages/HtmlCssJsPlaygroundPage'));
const MergePdfPage = lazyWithPreload(() => import('./pages/MergePdfPage'));
const SplitPdfPage = lazyWithPreload(() => import('./pages/SplitPdfPage'));
const OrganizePdfPage = lazyWithPreload(() => import('./pages/OrganizePdfPage'));
const ImagesToPdfPage = lazyWithPreload(() => import('./pages/ImagesToPdfPage'));
const ImageToTextPage = lazyWithPreload(() => import('./pages/ImageToTextPage'));
const AddPageNumbersToPdfPage = lazyWithPreload(() => import('./pages/AddPageNumbersToPdfPage'));
const ComparePdfPage = lazyWithPreload(() => import('./pages/ComparePdfPage'));
const CompressPdfPage = lazyWithPreload(() => import('./pages/CompressPdfPage'));
const CropPdfPage = lazyWithPreload(() => import('./pages/CropPdfPage'));
const ExcelToPdfPage = lazyWithPreload(() => import('./pages/ExcelToPdfPage'));
const ExtractPdfPagesPage = lazyWithPreload(() => import('./pages/ExtractPdfPagesPage'));
const FlattenPdfPage = lazyWithPreload(() => import('./pages/FlattenPdfPage'));
const GrayscalePdfPage = lazyWithPreload(() => import('./pages/GrayscalePdfPage'));
const HtmlToPdfPage = lazyWithPreload(() => import('./pages/HtmlToPdfPage'));
const PdfMetadataEditorPage = lazyWithPreload(() => import('./pages/PdfMetadataEditorPage'));
const PdfToExcelPage = lazyWithPreload(() => import('./pages/PdfToExcelPage'));
const PdfToHtmlPage = lazyWithPreload(() => import('./pages/PdfToHtmlPage'));
const PdfToJpgPage = lazyWithPreload(() => import('./pages/PdfToJpgPage'));
const PdfToPowerpointPage = lazyWithPreload(() => import('./pages/PdfToPowerpointPage'));
const PdfToTextPage = lazyWithPreload(() => import('./pages/PdfToTextPage'));
const PdfToWordPage = lazyWithPreload(() => import('./pages/PdfToWordPage'));
const PowerpointToPdfPage = lazyWithPreload(() => import('./pages/PowerpointToPdfPage'));
const RemovePdfPagesPage = lazyWithPreload(() => import('./pages/RemovePdfPagesPage'));
const RepairPdfPage = lazyWithPreload(() => import('./pages/RepairPdfPage'));
const ResizePdfPagesPage = lazyWithPreload(() => import('./pages/ResizePdfPagesPage'));
const RotatePdfPage = lazyWithPreload(() => import('./pages/RotatePdfPage'));
const ScanToPdfPage = lazyWithPreload(() => import('./pages/ScanToPdfPage'));
const ProtectPdfPage = lazyWithPreload(() => import('./pages/ProtectPdfPage'));
const UnlockPdfPage = lazyWithPreload(() => import('./pages/UnlockPdfPage'));
const SignPdfPage = lazyWithPreload(() => import('./pages/SignPdfPage'));
const EditPdfPage = lazyWithPreload(() => import('./pages/EditPdfPage'));
const RedactPdfPage = lazyWithPreload(() => import('./pages/RedactPdfPage'));
const OcrPdfPage = lazyWithPreload(() => import('./pages/OcrPdfPage'));
const PdfToPdfaPage = lazyWithPreload(() => import('./pages/PdfToPdfaPage'));
const AddHeaderFooterToPdfPage = lazyWithPreload(() => import('./pages/AddHeaderFooterToPdfPage'));
const NUpPdfPage = lazyWithPreload(() => import('./pages/NUpPdfPage'));
const TextToPdfPage = lazyWithPreload(() => import('./pages/TextToPdfPage'));
const WatermarkPdfPage = lazyWithPreload(() => import('./pages/WatermarkPdfPage'));
const WordToPdfPage = lazyWithPreload(() => import('./pages/WordToPdfPage'));
const MyDeviceInfoPage = lazyWithPreload(() => import('./pages/MyDeviceInfoPage'));
const PgpKeyGeneratorPage = lazyWithPreload(() => import('./pages/PgpKeyGeneratorPage'));
const WhatsappLinkGeneratorPage = lazyWithPreload(() => import('./pages/WhatsappLinkGeneratorPage'));
const ImageCropperPage = lazyWithPreload(() => import('./pages/ImageCropperPage'));
const ImageColorExtractorPage = lazyWithPreload(() => import('./pages/ImageColorExtractorPage'));
const RemoveDuplicateLinesPage = lazyWithPreload(() => import('./pages/RemoveDuplicateLinesPage'));
const MacAddressLookupPage = lazyWithPreload(() => import('./pages/MacAddressLookupPage'));
const Ipv6UlaGeneratorPage = lazyWithPreload(() => import('./pages/Ipv6UlaGeneratorPage'));
const EntropyCalculatorPage = lazyWithPreload(() => import('./pages/EntropyCalculatorPage'));
const HexDumpPage = lazyWithPreload(() => import('./pages/HexDumpPage'));
const FileTypeCheckerPage = lazyWithPreload(() => import('./pages/FileTypeCheckerPage'));
const FixTextEncodingPage = lazyWithPreload(() => import('./pages/FixTextEncodingPage'));
const DefangUrlPage = lazyWithPreload(() => import('./pages/DefangUrlPage'));
const NatoPhoneticAlphabetPage = lazyWithPreload(() => import('./pages/NatoPhoneticAlphabetPage'));
const RomanNumeralConverterPage = lazyWithPreload(() => import('./pages/RomanNumeralConverterPage'));
const BasicAuthGeneratorPage = lazyWithPreload(() => import('./pages/BasicAuthGeneratorPage'));
const HexToRgbPage = lazyWithPreload(() => import('./pages/HexToRgbPage'));
const HexToDecimalPage = lazyWithPreload(() => import('./pages/HexToDecimalPage'));
const Base64ToImagePage = lazyWithPreload(() => import('./pages/Base64ToImagePage'));
const EpochToDatePage = lazyWithPreload(() => import('./pages/EpochToDatePage'));
const RgbToHexPage = lazyWithPreload(() => import('./pages/RgbToHexPage'));
const DecimalToHexPage = lazyWithPreload(() => import('./pages/DecimalToHexPage'));
const DecimalToBinaryPage = lazyWithPreload(() => import('./pages/DecimalToBinaryPage'));
const BinaryToDecimalPage = lazyWithPreload(() => import('./pages/BinaryToDecimalPage'));
const MorseCodeTranslatorPage = lazyWithPreload(() => import('./pages/MorseCodeTranslatorPage'));
const RailFenceCipherPage = lazyWithPreload(() => import('./pages/RailFenceCipherPage'));
const AffineCipherPage = lazyWithPreload(() => import('./pages/AffineCipherPage'));
const AtbashCipherPage = lazyWithPreload(() => import('./pages/AtbashCipherPage'));
const Rot13Page = lazyWithPreload(() => import('./pages/Rot13Page'));
const VigenereCipherPage = lazyWithPreload(() => import('./pages/VigenereCipherPage'));
const CaesarCipherPage = lazyWithPreload(() => import('./pages/CaesarCipherPage'));
const Asn1DecoderPage = lazyWithPreload(() => import('./pages/Asn1DecoderPage'));
const CborDecoderPage = lazyWithPreload(() => import('./pages/CborDecoderPage'));
const ProtobufDecoderPage = lazyWithPreload(() => import('./pages/ProtobufDecoderPage'));
const MsgpackDecoderPage = lazyWithPreload(() => import('./pages/MsgpackDecoderPage'));
const BencodeDecoderPage = lazyWithPreload(() => import('./pages/BencodeDecoderPage'));
const DoubleUrlEncodePage = lazyWithPreload(() => import('./pages/DoubleUrlEncodePage'));
const PunycodeConverterPage = lazyWithPreload(() => import('./pages/PunycodeConverterPage'));
const Base58DecoderPage = lazyWithPreload(() => import('./pages/Base58DecoderPage'));
const Base85EncoderPage = lazyWithPreload(() => import('./pages/Base85EncoderPage'));
const BrotliCompressorPage = lazyWithPreload(() => import('./pages/BrotliCompressorPage'));
const ZlibInflatePage = lazyWithPreload(() => import('./pages/ZlibInflatePage'));
const GzipCompressorPage = lazyWithPreload(() => import('./pages/GzipCompressorPage'));
const NumberToWordsPage = lazyWithPreload(() => import('./pages/NumberToWordsPage'));
const FakeCsvGeneratorPage = lazyWithPreload(() => import('./pages/FakeCsvGeneratorPage'));
const JsonFlattenPage = lazyWithPreload(() => import('./pages/JsonFlattenPage'));
const JsonSortKeysPage = lazyWithPreload(() => import('./pages/JsonSortKeysPage'));
const WeekNumberPage = lazyWithPreload(() => import('./pages/WeekNumberPage'));
const DateFormatConverterPage = lazyWithPreload(() => import('./pages/DateFormatConverterPage'));
const DateCalculatorPage = lazyWithPreload(() => import('./pages/DateCalculatorPage'));
const DateDifferencePage = lazyWithPreload(() => import('./pages/DateDifferencePage'));
const UtmBuilderPage = lazyWithPreload(() => import('./pages/UtmBuilderPage'));
const TotpGeneratorPage = lazyWithPreload(() => import('./pages/TotpGeneratorPage'));
const LuhnCheckerPage = lazyWithPreload(() => import('./pages/LuhnCheckerPage'));
const CookieParserPage = lazyWithPreload(() => import('./pages/CookieParserPage'));
const HttpHeaderParserPage = lazyWithPreload(() => import('./pages/HttpHeaderParserPage'));
const PhoneNumberFormatterPage = lazyWithPreload(() => import('./pages/PhoneNumberFormatterPage'));
const EmailValidatorPage = lazyWithPreload(() => import('./pages/EmailValidatorPage'));
const DnsLookupPage = lazyWithPreload(() => import('./pages/DnsLookupPage'));
const MacAddressFormatterPage = lazyWithPreload(() => import('./pages/MacAddressFormatterPage'));
const IpConverterPage = lazyWithPreload(() => import('./pages/IpConverterPage'));
const CssPatternGeneratorPage = lazyWithPreload(() => import('./pages/CssPatternGeneratorPage'));
const CssTriangleGeneratorPage = lazyWithPreload(() => import('./pages/CssTriangleGeneratorPage'));
const CssLoaderGeneratorPage = lazyWithPreload(() => import('./pages/CssLoaderGeneratorPage'));
const GlassmorphismGeneratorPage = lazyWithPreload(() => import('./pages/GlassmorphismGeneratorPage'));
const CssClipPathGeneratorPage = lazyWithPreload(() => import('./pages/CssClipPathGeneratorPage'));
const CubicBezierGeneratorPage = lazyWithPreload(() => import('./pages/CubicBezierGeneratorPage'));
const ExtractEmailsFromTextPage = lazyWithPreload(() => import('./pages/ExtractEmailsFromTextPage'));
const AsciiArtGeneratorPage = lazyWithPreload(() => import('./pages/AsciiArtGeneratorPage'));
const JavascriptKeycodePage = lazyWithPreload(() => import('./pages/JavascriptKeycodePage'));
const SchemaMarkupGeneratorPage = lazyWithPreload(() => import('./pages/SchemaMarkupGeneratorPage'));
const CodeToImagePage = lazyWithPreload(() => import('./pages/CodeToImagePage'));
const DockerRunToComposePage = lazyWithPreload(() => import('./pages/DockerRunToComposePage'));
const CssToReactNativePage = lazyWithPreload(() => import('./pages/CssToReactNativePage'));
const ReactNativeShadowGeneratorPage = lazyWithPreload(() => import('./pages/ReactNativeShadowGeneratorPage'));
const ZodToTypescriptPage = lazyWithPreload(() => import('./pages/ZodToTypescriptPage'));
const GraphqlToTypescriptPage = lazyWithPreload(() => import('./pages/GraphqlToTypescriptPage'));
const MarkdownToJsxPage = lazyWithPreload(() => import('./pages/MarkdownToJsxPage'));
const MarkdownToHtmlPage = lazyWithPreload(() => import('./pages/MarkdownToHtmlPage'));
const HtmlToMarkdownPage = lazyWithPreload(() => import('./pages/HtmlToMarkdownPage'));
const HtmlToJsxPage = lazyWithPreload(() => import('./pages/HtmlToJsxPage'));
const GithubActionsValidatorPage = lazyWithPreload(() => import('./pages/GithubActionsValidatorPage'));
const PortNumbersPage = lazyWithPreload(() => import('./pages/PortNumbersPage'));
const MimeTypesPage = lazyWithPreload(() => import('./pages/MimeTypesPage'));
const HttpStatusCodesPage = lazyWithPreload(() => import('./pages/HttpStatusCodesPage'));
const SemverCalculatorPage = lazyWithPreload(() => import('./pages/SemverCalculatorPage'));
const SshKeyGeneratorPage = lazyWithPreload(() => import('./pages/SshKeyGeneratorPage'));
const CrontabGeneratorPage = lazyWithPreload(() => import('./pages/CrontabGeneratorPage'));
const ChmodCalculatorPage = lazyWithPreload(() => import('./pages/ChmodCalculatorPage'));
const HtaccessGeneratorPage = lazyWithPreload(() => import('./pages/HtaccessGeneratorPage'));
const NginxConfigGeneratorPage = lazyWithPreload(() => import('./pages/NginxConfigGeneratorPage'));
const GitignoreGeneratorPage = lazyWithPreload(() => import('./pages/GitignoreGeneratorPage'));
const EnvValidatorPage = lazyWithPreload(() => import('./pages/EnvValidatorPage'));
const KubernetesYamlValidatorPage = lazyWithPreload(() => import('./pages/KubernetesYamlValidatorPage'));
const DockerfileLinterPage = lazyWithPreload(() => import('./pages/DockerfileLinterPage'));
const DockerComposeGeneratorPage = lazyWithPreload(() => import('./pages/DockerComposeGeneratorPage'));
const BorderRadiusGeneratorPage = lazyWithPreload(() => import('./pages/BorderRadiusGeneratorPage'));
const PlaceholderImagePage = lazyWithPreload(() => import('./pages/PlaceholderImagePage'));
const SvgToPngPage = lazyWithPreload(() => import('./pages/SvgToPngPage'));
const ExifRemoverPage = lazyWithPreload(() => import('./pages/ExifRemoverPage'));
const ImageCompressorPage = lazyWithPreload(() => import('./pages/ImageCompressorPage'));
const ImageToWebpPage = lazyWithPreload(() => import('./pages/ImageToWebpPage'));
const FlexboxGeneratorPage = lazyWithPreload(() => import('./pages/FlexboxGeneratorPage'));
const CssGridGeneratorPage = lazyWithPreload(() => import('./pages/CssGridGeneratorPage'));
const ColorPaletteGeneratorPage = lazyWithPreload(() => import('./pages/ColorPaletteGeneratorPage'));
const AspectRatioCalculatorPage = lazyWithPreload(() => import('./pages/AspectRatioCalculatorPage'));
const CssClampGeneratorPage = lazyWithPreload(() => import('./pages/CssClampGeneratorPage'));
const PxToRemPage = lazyWithPreload(() => import('./pages/PxToRemPage'));
const PrivateKeyInspectorPage = lazyWithPreload(() => import('./pages/PrivateKeyInspectorPage'));
const PfxViewerPage = lazyWithPreload(() => import('./pages/PfxViewerPage'));
const CsrDecoderPage = lazyWithPreload(() => import('./pages/CsrDecoderPage'));
const HmacGeneratorPage = lazyWithPreload(() => import('./pages/HmacGeneratorPage'));
const Pbkdf2GeneratorPage = lazyWithPreload(() => import('./pages/Pbkdf2GeneratorPage'));
const ScryptGeneratorPage = lazyWithPreload(() => import('./pages/ScryptGeneratorPage'));
const Argon2GeneratorPage = lazyWithPreload(() => import('./pages/Argon2GeneratorPage'));
const BcryptGeneratorPage = lazyWithPreload(() => import('./pages/BcryptGeneratorPage'));
const YamlToPropertiesPage = lazyWithPreload(() => import('./pages/YamlToPropertiesPage'));
const PropertiesToJsonPage = lazyWithPreload(() => import('./pages/PropertiesToJsonPage'));
const PropertiesToYamlPage = lazyWithPreload(() => import('./pages/PropertiesToYamlPage'));
const TomlToCsvPage = lazyWithPreload(() => import('./pages/TomlToCsvPage'));
const YamlToCsvPage = lazyWithPreload(() => import('./pages/YamlToCsvPage'));
const XmlToCsvPage = lazyWithPreload(() => import('./pages/XmlToCsvPage'));
const XmlToYamlPage = lazyWithPreload(() => import('./pages/XmlToYamlPage'));
const XmlToJsonPage = lazyWithPreload(() => import('./pages/XmlToJsonPage'));
const SqlToJsonPage = lazyWithPreload(() => import('./pages/SqlToJsonPage'));
const JsonToSqlPage = lazyWithPreload(() => import('./pages/JsonToSqlPage'));
const CsvToSqlPage = lazyWithPreload(() => import('./pages/CsvToSqlPage'));
const CsvToMarkdownPage = lazyWithPreload(() => import('./pages/CsvToMarkdownPage'));
const CsvToYamlPage = lazyWithPreload(() => import('./pages/CsvToYamlPage'));
const CsvToXmlPage = lazyWithPreload(() => import('./pages/CsvToXmlPage'));
const JsonToHtmlTablePage = lazyWithPreload(() => import('./pages/JsonToHtmlTablePage'));
const ExcelToJsonPage = lazyWithPreload(() => import('./pages/ExcelToJsonPage'));
const JsonToExcelPage = lazyWithPreload(() => import('./pages/JsonToExcelPage'));
const CsvToJsonPage = lazyWithPreload(() => import('./pages/CsvToJsonPage'));
const JsonToCsvPage = lazyWithPreload(() => import('./pages/JsonToCsvPage'));
const UnicodeEscapePage = lazyWithPreload(() => import('./pages/UnicodeEscapePage'));
const HtmlToTextPage = lazyWithPreload(() => import('./pages/HtmlToTextPage'));
const CsvViewerPage = lazyWithPreload(() => import('./pages/CsvViewerPage'));
const QrCodeScannerPage = lazyWithPreload(() => import('./pages/QrCodeScannerPage'));
const BarcodeGeneratorPage = lazyWithPreload(() => import('./pages/BarcodeGeneratorPage'));
const RandomStringGeneratorPage = lazyWithPreload(() => import('./pages/RandomStringGeneratorPage'));
const TextToBinaryPage = lazyWithPreload(() => import('./pages/TextToBinaryPage'));
const UnicodeInspectorPage = lazyWithPreload(() => import('./pages/UnicodeInspectorPage'));
const JsonEscapePage = lazyWithPreload(() => import('./pages/JsonEscapePage'));
const WordCounterPage = lazyWithPreload(() => import('./pages/WordCounterPage'));
const MarkdownEditorPage = lazyWithPreload(() => import('./pages/MarkdownEditorPage'));
const HtmlBeautifierPage = lazyWithPreload(() => import('./pages/HtmlBeautifierPage'));
const HtmlMinifierPage = lazyWithPreload(() => import('./pages/HtmlMinifierPage'));
const CssBeautifierPage = lazyWithPreload(() => import('./pages/CssBeautifierPage'));
const CssMinifierPage = lazyWithPreload(() => import('./pages/CssMinifierPage'));
const JavascriptBeautifierPage = lazyWithPreload(() => import('./pages/JavascriptBeautifierPage'));
const JavascriptMinifierPage = lazyWithPreload(() => import('./pages/JavascriptMinifierPage'));
const JwtEncoderPage = lazyWithPreload(() => import('./pages/JwtEncoderPage'));
const RegexTesterPage = lazyWithPreload(() => import('./pages/RegexTesterPage'));
const JsonEditorPage = lazyWithPreload(() => import('./pages/JsonEditorPage'));
const JsonVisualizerPage = lazyWithPreload(() => import('./pages/JsonVisualizerPage'));
const JqPlaygroundPage = lazyWithPreload(() => import('./pages/JqPlaygroundPage'));
const JsonToJpaEntityPage = lazyWithPreload(() => import('./pages/JsonToJpaEntityPage'));
const JsonToJsonSchemaPage = lazyWithPreload(() => import('./pages/JsonToJsonSchemaPage'));
const JsonToZodPage = lazyWithPreload(() => import('./pages/JsonToZodPage'));
const JsonToSwiftPage = lazyWithPreload(() => import('./pages/JsonToSwiftPage'));
const JsonToKotlinPage = lazyWithPreload(() => import('./pages/JsonToKotlinPage'));
const JsonToCppPage = lazyWithPreload(() => import('./pages/JsonToCppPage'));
const JsonToPhpPage = lazyWithPreload(() => import('./pages/JsonToPhpPage'));
const JsonToDartPage = lazyWithPreload(() => import('./pages/JsonToDartPage'));
const JsonToPythonPage = lazyWithPreload(() => import('./pages/JsonToPythonPage'));
const JsonToJavaPage = lazyWithPreload(() => import('./pages/JsonToJavaPage'));
const JsonToCsharpPage = lazyWithPreload(() => import('./pages/JsonToCsharpPage'));
const JsonToRustPage = lazyWithPreload(() => import('./pages/JsonToRustPage'));
const JsonToGoPage = lazyWithPreload(() => import('./pages/JsonToGoPage'));
const JsonToTypescriptPage = lazyWithPreload(() => import('./pages/JsonToTypescriptPage'));
const HubPage = lazyWithPreload(() => import('./pages/HubPage'));
// Category hub pages: one component, the route picks the hub (src/data/hubs.ts).
const hubRoute = (id: string) => Object.assign(() => <HubPage id={id} />, { preload: () => HubPage.preload() });

// Every real page route (aliases and the 404 catch-all are added separately
// below). Kept as data so main.tsx can preload the initial route's chunk via
// preloadRoute() before mounting — see utils/lazyWithPreload.ts.
const PAGE_ROUTES: { path: string; Component: PreloadableComponent<object> }[] = [
  { path: '/', Component: Home },
  { path: '/byte-parser', Component: ByteParserPage },
  { path: '/json-parser', Component: JsonParserPage },
  { path: '/json-schema-validator', Component: JsonSchemaValidatorPage },
  { path: '/url-parser', Component: UrlParserPage },
  { path: '/query-string-parser', Component: QueryStringPage },
  { path: '/rest-api-tester', Component: RestApiTesterPage },
  { path: '/timezone-converter', Component: TimezoneConverterPage },
  { path: '/color-blindness-simulator', Component: ColorBlindnessPage },
  { path: '/css-effects-generator', Component: CssEffectsGeneratorPage },
  { path: '/meta-preview', Component: MetaPreviewPage },
  { path: '/image-resizer', Component: ImageResizerPage },
  { path: '/jsonpath-tester', Component: JsonPathPage },
  { path: '/xml-xsd-validator', Component: XmlXsdValidatorPage },
  { path: '/certificate-decoder', Component: CertificateDecoderPage },
  { path: '/number-byte-formatter', Component: NumberByteFormatterPage },
  { path: '/json-toml-converter', Component: JsonTomlPage },
  { path: '/iban-validator', Component: IbanValidatorPage },
  { path: '/string-list-tools', Component: StringListToolsPage },
  { path: '/qr-code-generator', Component: QrCodeGeneratorPage },
  { path: '/guid-generator', Component: GuidGeneratorPage },
  { path: '/json-compare', Component: JsonComparePage },
  { path: '/text-compare', Component: TextComparePage },
  { path: '/json-beautifier', Component: JsonBeautifierPage },
  { path: '/base-converter', Component: BaseConverterPage },
  { path: '/xml-formatter', Component: XmlFormatterPage },
  { path: '/encode-decode', Component: EncodeDecodePage },
  { path: '/jwt-decoder', Component: JwtDecoderPage },
  { path: '/hash-generator', Component: HashGeneratorPage },
  { path: '/password-generator', Component: PasswordGeneratorPage },
  { path: '/password-strength-checker', Component: PasswordStrengthPage },
  { path: '/unix-timestamp-converter', Component: UnixTimestampConverterPage },
  { path: '/cron-parser', Component: CronParserPage },
  { path: '/case-converter', Component: CaseConverterPage },
  { path: '/slug-generator', Component: SlugGeneratorPage },
  { path: '/yaml-json-converter', Component: YamlJsonConverterPage },
  { path: '/curl-converter', Component: CurlConverterPage },
  { path: '/sql-formatter', Component: SqlFormatterPage },
  { path: '/user-agent-parser', Component: UserAgentParserPage },
  { path: '/svg-optimizer', Component: SvgOptimizerPage },
  { path: '/color-converter', Component: ColorConverterPage },
  { path: '/dummy-data-generator', Component: DummyDataGeneratorPage },
  { path: '/lorem-ipsum', Component: LoremIpsumPage },
  { path: '/graphql-formatter', Component: GraphqlFormatterPage },
  { path: '/cidr-calculator', Component: CidrCalculatorPage },
  { path: '/html-entity-encoder', Component: HtmlEntityEncoderPage },
  { path: '/diff-checker', Component: DiffCheckerPage },
  { path: '/image-base64', Component: Base64ImagePage },
  { path: '/favicon-generator', Component: FaviconGeneratorPage },
  { path: '/csp-generator', Component: CspGeneratorPage },
  { path: '/robotstxt-generator', Component: RobotsTxtGeneratorPage },
  { path: '/aes-encryption', Component: AesEncryptionPage },
  { path: '/base64url-converter', Component: Base64UrlConverterPage },
  { path: '/rsa-encryption', Component: RsaEncryptionPage },
  { path: '/json-to-typescript', Component: JsonToTypescriptPage },
  { path: '/json-to-go', Component: JsonToGoPage },
  { path: '/json-to-rust', Component: JsonToRustPage },
  { path: '/json-to-csharp', Component: JsonToCsharpPage },
  { path: '/json-to-java', Component: JsonToJavaPage },
  { path: '/json-to-python', Component: JsonToPythonPage },
  { path: '/json-to-dart', Component: JsonToDartPage },
  { path: '/json-to-php', Component: JsonToPhpPage },
  { path: '/json-to-cpp', Component: JsonToCppPage },
  { path: '/json-to-kotlin', Component: JsonToKotlinPage },
  { path: '/json-to-swift', Component: JsonToSwiftPage },
  { path: '/json-to-zod', Component: JsonToZodPage },
  { path: '/json-to-json-schema', Component: JsonToJsonSchemaPage },
  { path: '/json-to-jpa-entity', Component: JsonToJpaEntityPage },
  { path: '/jq-playground', Component: JqPlaygroundPage },
  { path: '/json-visualizer', Component: JsonVisualizerPage },
  { path: '/json-editor', Component: JsonEditorPage },
  { path: '/regex-tester', Component: RegexTesterPage },
  { path: '/jwt-encoder', Component: JwtEncoderPage },
  { path: '/javascript-minifier', Component: JavascriptMinifierPage },
  { path: '/javascript-beautifier', Component: JavascriptBeautifierPage },
  { path: '/css-minifier', Component: CssMinifierPage },
  { path: '/css-beautifier', Component: CssBeautifierPage },
  { path: '/html-minifier', Component: HtmlMinifierPage },
  { path: '/html-beautifier', Component: HtmlBeautifierPage },
  { path: '/markdown-editor', Component: MarkdownEditorPage },
  { path: '/word-counter', Component: WordCounterPage },
  { path: '/json-escape', Component: JsonEscapePage },
  { path: '/unicode-inspector', Component: UnicodeInspectorPage },
  { path: '/text-to-binary', Component: TextToBinaryPage },
  { path: '/random-string-generator', Component: RandomStringGeneratorPage },
  { path: '/barcode-generator', Component: BarcodeGeneratorPage },
  { path: '/qr-code-scanner', Component: QrCodeScannerPage },
  { path: '/csv-viewer', Component: CsvViewerPage },
  { path: '/html-to-text', Component: HtmlToTextPage },
  { path: '/unicode-escape', Component: UnicodeEscapePage },
  { path: '/json-to-csv', Component: JsonToCsvPage },
  { path: '/csv-to-json', Component: CsvToJsonPage },
  { path: '/json-to-excel', Component: JsonToExcelPage },
  { path: '/excel-to-json', Component: ExcelToJsonPage },
  { path: '/json-to-html-table', Component: JsonToHtmlTablePage },
  { path: '/csv-to-xml', Component: CsvToXmlPage },
  { path: '/csv-to-yaml', Component: CsvToYamlPage },
  { path: '/csv-to-markdown', Component: CsvToMarkdownPage },
  { path: '/csv-to-sql', Component: CsvToSqlPage },
  { path: '/json-to-sql', Component: JsonToSqlPage },
  { path: '/sql-to-json', Component: SqlToJsonPage },
  { path: '/xml-to-json', Component: XmlToJsonPage },
  { path: '/xml-to-yaml', Component: XmlToYamlPage },
  { path: '/xml-to-csv', Component: XmlToCsvPage },
  { path: '/yaml-to-csv', Component: YamlToCsvPage },
  { path: '/toml-to-csv', Component: TomlToCsvPage },
  { path: '/properties-to-yaml', Component: PropertiesToYamlPage },
  { path: '/properties-to-json', Component: PropertiesToJsonPage },
  { path: '/yaml-to-properties', Component: YamlToPropertiesPage },
  { path: '/bcrypt-generator', Component: BcryptGeneratorPage },
  { path: '/argon2-generator', Component: Argon2GeneratorPage },
  { path: '/scrypt-generator', Component: ScryptGeneratorPage },
  { path: '/pbkdf2-generator', Component: Pbkdf2GeneratorPage },
  { path: '/hmac-generator', Component: HmacGeneratorPage },
  { path: '/csr-decoder', Component: CsrDecoderPage },
  { path: '/pfx-viewer', Component: PfxViewerPage },
  { path: '/private-key-inspector', Component: PrivateKeyInspectorPage },
  { path: '/px-to-rem', Component: PxToRemPage },
  { path: '/css-clamp-generator', Component: CssClampGeneratorPage },
  { path: '/aspect-ratio-calculator', Component: AspectRatioCalculatorPage },
  { path: '/color-palette-generator', Component: ColorPaletteGeneratorPage },
  { path: '/css-grid-generator', Component: CssGridGeneratorPage },
  { path: '/flexbox-generator', Component: FlexboxGeneratorPage },
  { path: '/image-to-webp', Component: ImageToWebpPage },
  { path: '/image-compressor', Component: ImageCompressorPage },
  { path: '/exif-remover', Component: ExifRemoverPage },
  { path: '/svg-to-png', Component: SvgToPngPage },
  { path: '/placeholder-image', Component: PlaceholderImagePage },
  { path: '/border-radius-generator', Component: BorderRadiusGeneratorPage },
  { path: '/docker-compose-generator', Component: DockerComposeGeneratorPage },
  { path: '/dockerfile-linter', Component: DockerfileLinterPage },
  { path: '/kubernetes-yaml-validator', Component: KubernetesYamlValidatorPage },
  { path: '/env-validator', Component: EnvValidatorPage },
  { path: '/gitignore-generator', Component: GitignoreGeneratorPage },
  { path: '/nginx-config-generator', Component: NginxConfigGeneratorPage },
  { path: '/htaccess-generator', Component: HtaccessGeneratorPage },
  { path: '/chmod-calculator', Component: ChmodCalculatorPage },
  { path: '/crontab-generator', Component: CrontabGeneratorPage },
  { path: '/ssh-key-generator', Component: SshKeyGeneratorPage },
  { path: '/semver-calculator', Component: SemverCalculatorPage },
  { path: '/http-status-codes', Component: HttpStatusCodesPage },
  { path: '/mime-types', Component: MimeTypesPage },
  { path: '/port-numbers', Component: PortNumbersPage },
  { path: '/github-actions-validator', Component: GithubActionsValidatorPage },
  { path: '/html-to-jsx', Component: HtmlToJsxPage },
  { path: '/html-to-markdown', Component: HtmlToMarkdownPage },
  { path: '/markdown-to-html', Component: MarkdownToHtmlPage },
  { path: '/markdown-to-jsx', Component: MarkdownToJsxPage },
  { path: '/graphql-to-typescript', Component: GraphqlToTypescriptPage },
  { path: '/zod-to-typescript', Component: ZodToTypescriptPage },
  { path: '/react-native-shadow-generator', Component: ReactNativeShadowGeneratorPage },
  { path: '/css-to-react-native', Component: CssToReactNativePage },
  { path: '/docker-run-to-compose', Component: DockerRunToComposePage },
  { path: '/code-to-image', Component: CodeToImagePage },
  { path: '/schema-markup-generator', Component: SchemaMarkupGeneratorPage },
  { path: '/javascript-keycode', Component: JavascriptKeycodePage },
  { path: '/ascii-art-generator', Component: AsciiArtGeneratorPage },
  { path: '/extract-emails-from-text', Component: ExtractEmailsFromTextPage },
  { path: '/cubic-bezier-generator', Component: CubicBezierGeneratorPage },
  { path: '/css-clip-path-generator', Component: CssClipPathGeneratorPage },
  { path: '/glassmorphism-generator', Component: GlassmorphismGeneratorPage },
  { path: '/css-loader-generator', Component: CssLoaderGeneratorPage },
  { path: '/css-triangle-generator', Component: CssTriangleGeneratorPage },
  { path: '/css-pattern-generator', Component: CssPatternGeneratorPage },
  { path: '/ip-converter', Component: IpConverterPage },
  { path: '/mac-address-formatter', Component: MacAddressFormatterPage },
  { path: '/dns-lookup', Component: DnsLookupPage },
  { path: '/email-validator', Component: EmailValidatorPage },
  { path: '/phone-number-formatter', Component: PhoneNumberFormatterPage },
  { path: '/http-header-parser', Component: HttpHeaderParserPage },
  { path: '/cookie-parser', Component: CookieParserPage },
  { path: '/luhn-checker', Component: LuhnCheckerPage },
  { path: '/totp-generator', Component: TotpGeneratorPage },
  { path: '/utm-builder', Component: UtmBuilderPage },
  { path: '/date-difference', Component: DateDifferencePage },
  { path: '/date-calculator', Component: DateCalculatorPage },
  { path: '/date-format-converter', Component: DateFormatConverterPage },
  { path: '/week-number', Component: WeekNumberPage },
  { path: '/json-sort-keys', Component: JsonSortKeysPage },
  { path: '/json-flatten', Component: JsonFlattenPage },
  { path: '/fake-csv-generator', Component: FakeCsvGeneratorPage },
  { path: '/number-to-words', Component: NumberToWordsPage },
  { path: '/gzip-compressor', Component: GzipCompressorPage },
  { path: '/zlib-inflate', Component: ZlibInflatePage },
  { path: '/brotli-compressor', Component: BrotliCompressorPage },
  { path: '/base85-encoder', Component: Base85EncoderPage },
  { path: '/base58-decoder', Component: Base58DecoderPage },
  { path: '/punycode-converter', Component: PunycodeConverterPage },
  { path: '/double-url-encode', Component: DoubleUrlEncodePage },
  { path: '/bencode-decoder', Component: BencodeDecoderPage },
  { path: '/msgpack-decoder', Component: MsgpackDecoderPage },
  { path: '/protobuf-decoder', Component: ProtobufDecoderPage },
  { path: '/cbor-decoder', Component: CborDecoderPage },
  { path: '/asn1-decoder', Component: Asn1DecoderPage },
  { path: '/caesar-cipher', Component: CaesarCipherPage },
  { path: '/vigenere-cipher', Component: VigenereCipherPage },
  { path: '/rot13', Component: Rot13Page },
  { path: '/atbash-cipher', Component: AtbashCipherPage },
  { path: '/affine-cipher', Component: AffineCipherPage },
  { path: '/rail-fence-cipher', Component: RailFenceCipherPage },
  { path: '/morse-code-translator', Component: MorseCodeTranslatorPage },
  { path: '/binary-to-decimal', Component: BinaryToDecimalPage },
  { path: '/decimal-to-binary', Component: DecimalToBinaryPage },
  { path: '/decimal-to-hex', Component: DecimalToHexPage },
  { path: '/rgb-to-hex', Component: RgbToHexPage },
  { path: '/epoch-to-date', Component: EpochToDatePage },
  { path: '/base64-to-image', Component: Base64ToImagePage },
  { path: '/hex-to-decimal', Component: HexToDecimalPage },
  { path: '/hex-to-rgb', Component: HexToRgbPage },
  { path: '/merge-pdf', Component: MergePdfPage },
  { path: '/split-pdf', Component: SplitPdfPage },
  { path: '/organize-pdf', Component: OrganizePdfPage },
  { path: '/images-to-pdf', Component: ImagesToPdfPage },
  { path: '/image-to-text', Component: ImageToTextPage },
  { path: '/compress-pdf', Component: CompressPdfPage },
  { path: '/rotate-pdf', Component: RotatePdfPage },
  { path: '/remove-pdf-pages', Component: RemovePdfPagesPage },
  { path: '/extract-pdf-pages', Component: ExtractPdfPagesPage },
  { path: '/add-page-numbers-to-pdf', Component: AddPageNumbersToPdfPage },
  { path: '/watermark-pdf', Component: WatermarkPdfPage },
  { path: '/crop-pdf', Component: CropPdfPage },
  { path: '/resize-pdf-pages', Component: ResizePdfPagesPage },
  { path: '/flatten-pdf', Component: FlattenPdfPage },
  { path: '/grayscale-pdf', Component: GrayscalePdfPage },
  { path: '/pdf-metadata-editor', Component: PdfMetadataEditorPage },
  { path: '/repair-pdf', Component: RepairPdfPage },
  { path: '/pdf-to-jpg', Component: PdfToJpgPage },
  { path: '/pdf-to-text', Component: PdfToTextPage },
  { path: '/pdf-to-word', Component: PdfToWordPage },
  { path: '/pdf-to-excel', Component: PdfToExcelPage },
  { path: '/pdf-to-powerpoint', Component: PdfToPowerpointPage },
  { path: '/pdf-to-html', Component: PdfToHtmlPage },
  { path: '/html-to-pdf', Component: HtmlToPdfPage },
  { path: '/text-to-pdf', Component: TextToPdfPage },
  { path: '/word-to-pdf', Component: WordToPdfPage },
  { path: '/excel-to-pdf', Component: ExcelToPdfPage },
  { path: '/powerpoint-to-pdf', Component: PowerpointToPdfPage },
  { path: '/compare-pdf', Component: ComparePdfPage },
  { path: '/scan-to-pdf', Component: ScanToPdfPage },
  { path: '/protect-pdf', Component: ProtectPdfPage },
  { path: '/unlock-pdf', Component: UnlockPdfPage },
  { path: '/sign-pdf', Component: SignPdfPage },
  { path: '/edit-pdf', Component: EditPdfPage },
  { path: '/redact-pdf', Component: RedactPdfPage },
  { path: '/ocr-pdf', Component: OcrPdfPage },
  { path: '/pdf-to-pdfa', Component: PdfToPdfaPage },
  { path: '/add-header-footer-to-pdf', Component: AddHeaderFooterToPdfPage },
  { path: '/n-up-pdf', Component: NUpPdfPage },
  { path: '/basic-auth-generator', Component: BasicAuthGeneratorPage },
  { path: '/roman-numeral-converter', Component: RomanNumeralConverterPage },
  { path: '/nato-phonetic-alphabet', Component: NatoPhoneticAlphabetPage },
  { path: '/defang-url', Component: DefangUrlPage },
  { path: '/fix-text-encoding', Component: FixTextEncodingPage },
  { path: '/file-type-checker', Component: FileTypeCheckerPage },
  { path: '/hex-dump', Component: HexDumpPage },
  { path: '/entropy-calculator', Component: EntropyCalculatorPage },
  { path: '/ipv6-ula-generator', Component: Ipv6UlaGeneratorPage },
  { path: '/mac-address-lookup', Component: MacAddressLookupPage },
  { path: '/remove-duplicate-lines', Component: RemoveDuplicateLinesPage },
  { path: '/image-color-extractor', Component: ImageColorExtractorPage },
  { path: '/image-cropper', Component: ImageCropperPage },
  { path: '/whatsapp-link-generator', Component: WhatsappLinkGeneratorPage },
  { path: '/pgp-key-generator', Component: PgpKeyGeneratorPage },
  { path: '/my-device-info', Component: MyDeviceInfoPage },
  { path: '/html-css-js-playground', Component: HtmlCssJsPlaygroundPage },
  { path: '/javascript-playground', Component: JavascriptPlaygroundPage },
  { path: '/typescript-playground', Component: TypescriptPlaygroundPage },
  { path: '/react-playground', Component: ReactPlaygroundPage },
  { path: '/vue-playground', Component: VuePlaygroundPage },
  { path: '/svelte-playground', Component: SveltePlaygroundPage },
  { path: '/tailwind-playground', Component: TailwindPlaygroundPage },
  { path: '/sqlite-playground', Component: SqlitePlaygroundPage },
  { path: '/postgres-playground', Component: PostgresPlaygroundPage },
  { path: '/glsl-shader-playground', Component: GlslShaderPlaygroundPage },
  { path: '/python-playground', Component: PythonPlaygroundPage },
  { path: '/percentage-calculator', Component: PercentageCalculatorPage },
  { path: '/unit-converter', Component: UnitConverterPage },
  { path: '/loan-calculator', Component: LoanCalculatorPage },
  { path: '/compound-interest-calculator', Component: CompoundInterestCalculatorPage },
  { path: '/scientific-calculator', Component: ScientificCalculatorPage },
  { path: '/invert-pdf', Component: InvertPdfPage },
  { path: '/html-table-to-csv', Component: HtmlTableToCsvPage },
  { path: '/srt-vtt-converter', Component: SrtVttConverterPage },
  { path: '/pdf-to-svg', Component: PdfToSvgPage },
  { path: '/pdf-form-creator', Component: PdfFormCreatorPage },
  { path: '/about', Component: AboutPage },
  { path: '/contact', Component: ContactPage },
  { path: '/privacy-policy', Component: PrivacyPolicyPage },
  { path: '/terms', Component: TermsPage },
  // Hidden reference page: noindex, not in the sitemap, search index or any navigation.
  { path: '/ui-kit', Component: UiKitPage },
  { path: '/tools', Component: AllToolsPage },
  { path: '/saved', Component: SavedPage },
  { path: '/json-tools', Component: hubRoute('json-tools') },
  { path: '/converters', Component: hubRoute('converters') },
  { path: '/security-tools', Component: hubRoute('security-tools') },
  { path: '/frontend-tools', Component: hubRoute('frontend-tools') },
  { path: '/data-tools', Component: hubRoute('data-tools') },
  { path: '/devops-tools', Component: hubRoute('devops-tools') },
  { path: '/pdf-tools', Component: hubRoute('pdf-tools') },
  { path: '/playground-tools', Component: hubRoute('playground-tools') },
  { path: '/image-tools', Component: hubRoute('image-tools') },
];

// Matches like React Router does by default: case-insensitive, trailing
// slashes ignored. Unknown paths (aliases, 404) return undefined.
export function preloadRoute(pathname: string): Promise<unknown> | undefined {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return PAGE_ROUTES.find((route) => route.path === path)?.Component.preload();
}

const RouteLoadingFallback = () => (
  <div className="route-loading" role="status" aria-label="Loading">
    <div className="route-loading-spinner" />
  </div>
);

const App: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarEver, setSidebarEver] = useState(false);
  const [updateVersion, setUpdateVersion] = useState<string | null>(null);
  const [promptReady, setPromptReady] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Register SW and listen for updates
  useEffect(() => {
    registerServiceWorker((newVersion) => {
      setUpdateVersion(newVersion);
    });
  }, []);

  // Initialize install prompt
  useEffect(() => {
    initInstallPrompt(
      () => setPromptReady(true),
      () => setIsInstalled(true)
    );
  }, []);

  // Fetch the menu drawer's chunk when the browser is idle, so the first ☰ press opens it instantly.
  useEffect(() => {
    const load = () => void import('./components/SiteSidebar').catch(() => undefined);
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(load);
    else window.setTimeout(load, 2000);
  }, []);

  // Tells the inline AdSense loader in index.html that the first page has
  // rendered, so the ads script can load without competing with it.
  useEffect(() => {
    window.dispatchEvent(new Event('devstackkit:mounted'));
  }, []);

  return (
    <BrowserRouter>
      {updateVersion && (
        <UpdateBar version={updateVersion} onReload={() => skipWaiting()} />
      )}
      <a className="site-skip" href="#main">Skip to main content</a>
      <ScrollToTop />
      <IosInstallHint />
      <SiteHeader
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => {
          setSidebarEver(true);
          setSidebarOpen((open) => !open);
        }}
        promptReady={promptReady && !isInstalled}
        onInstalled={() => setIsInstalled(true)}
      />
      <div className="app-shell">
        {sidebarEver && (
          <Suspense fallback={null}>
            <SiteSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          </Suspense>
        )}
        <div className="app-content">
          <main id="main" tabIndex={-1}>
            {/* Inside <main> so the header, sidebar and footer stay usable
                when a page chunk fails to load. */}
            <RouteErrorBoundary>
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  {PAGE_ROUTES.map(({ path, Component }) => (
                    <Route key={path} path={path} element={<Component />} />
                  ))}

                  {/* Alias/typo redirects (glued compound words, shorthand,
                      hyphenated action phrases) so an alternate spelling of a
                      tool's name never 404s — generated from src/data/toolAliases.ts
                      rather than hand-written, since there are 160+ of them and
                      the source list is the thing that should stay readable. */}
                  {ALIAS_REDIRECTS.map(({ from, to }) => (
                    <Route key={from} path={from} element={<Navigate to={to} replace />} />
                  ))}

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </RouteErrorBoundary>
          </main>
          <SiteFooter />
        </div>
      </div>
      <AnalyticsTracker />
    </BrowserRouter>
  );
};

export default App;
